import {
    contextRepository,
    driveRepository,
    eventRepository,
    fragmentRepository,
    gameRepository,
    matchRepository,
    playRepository,
    statsRepository,
    transactionManager
} from "../storage/storage";
import PersistenceError from "./exceptions/persistence-error";
import SportNotSupportedError from "./exceptions/sport-not-supported-error";
import TennisGame from "../sports/tennis/tennis-game";
import FootballGame from "../sports/football/football-game";
import SportCodes from "../sports/common/sport-codes";
import ContextService from "./context-service";
import FootballSerializer from "./serializers/FootballSerializer";
import TennisSerializer from "./serializers/TennisSerializer";
import FlagFootballGame from "../sports/flag-football/flag-football-game";
import GamedayApiService from "./gameday-api-service";
import { EventStatusCode } from "../sports/common/event-status";

class GameService {
    constructor() {
        this.gameFactory = {
            [SportCodes.Football]: (event) => this.startFootballGame(event),
            [SportCodes.FlagFootball]: (event) =>
                this.startFlagFootballGame(event),
            [SportCodes.Tennis]: (event) => this.startTennisGame(event),
            [SportCodes.WomenTennis]: (event) => this.startTennisGame(event)
        };
    }

    async saveGame(game) {
        game.id = await gameRepository.put(game);

        return this.getGame(game.id);
    }

    async getGame(gameId) {
        const game = await gameRepository
            .where("id")
            .equals(gameId)
            .with({ plays: "play" })
            .then(async ([game]) => {
                return game;
            });
        if (game && game.plays) {
            game.plays = await playRepository.getPlaysWithFragments(game.id);
        }
        return game;
    }

    async getGameByEventId(eventId) {
        return gameRepository
            .where("eventId")
            .equals(eventId)
            .with({ plays: "play" })
            .then(async ([game]) => {
                if (game && game.plays) {
                    game.plays = await playRepository.getPlaysWithFragments(
                        game.id
                    );
                }

                return game;
            });
    }

    async getLatestGame() {
        const games = await gameRepository.getAll().sortBy("createdAt");

        if (games.length > 0) {
            const lastGame = games.pop();
            return this.getGame(lastGame.id);
        }

        return null;
    }

    async getAllGames() {
        return gameRepository.getAll().sortBy("createdAt");
    }

    async deleteGame(gameId) {
        try {
            return transactionManager.transaction(
                "rw",
                [
                    playRepository,
                    gameRepository,
                    contextRepository,
                    fragmentRepository,
                    driveRepository,
                    statsRepository,
                    matchRepository
                ],
                async () => {
                    const plays = await playRepository
                        .where("gameId")
                        .equals(gameId)
                        .toArray();
                    const playIds = plays.map((p) => p.id);

                    contextRepository.where("gameId").equals(gameId).delete();
                    statsRepository.where("gameId").equals(gameId).delete();
                    driveRepository.where("gameId").equals(gameId).delete();
                    matchRepository.where("gameId").equals(gameId).delete();
                    fragmentRepository.where("playId").anyOf(playIds).delete();
                    playRepository.where("id").anyOf(playIds).delete();

                    return gameRepository.delete(gameId);
                }
            );
        } catch (err) {
            throw new PersistenceError(err);
        }
    }

    async _startGame(eventId) {
        try {
            const event = await eventRepository.getById(eventId);
            const { sportId } = event;

            return this.gameFactory[sportId](event);
        } catch (err) {
            throw new SportNotSupportedError();
        }
    }

    async startTennisGame(event) {
        const gameId = await gameRepository.put(
            new TennisGame(event, event.sportId)
        );
        const game = await this.getGame(gameId);

        const gameContext = await ContextService.createContext(gameId);

        return {
            game,
            gameContext: TennisSerializer.serializeGameContext(gameContext)
        };
    }

    async startFootballGame(event) {
        const gameId = await gameRepository.put(new FootballGame(event));
        const game = await this.getGame(gameId);
        const gameContext = await ContextService.createContext(gameId);

        return {
            game,
            gameContext: FootballSerializer.serializeGameContext(gameContext)
        };
    }

    async startFlagFootballGame(event) {
        const gameId = await gameRepository.put(new FlagFootballGame(event));
        const game = await this.getGame(gameId);
        const gameContext = await ContextService.createContext(gameId);

        return {
            game,
            gameContext: FootballSerializer.serializeGameContext(gameContext)
        };
    }

    async deleteGameWhenEventStatusChanged(game) {
        if (game) {
            const localEvent = await eventRepository.getById(game.eventId);
            const remoteEvent = await GamedayApiService.getEventByExternalId(
                localEvent.eventId
            );
            if (
                remoteEvent.statusCode === EventStatusCode.NOT_STARTED &&
                localEvent.statusCode !== EventStatusCode.NOT_STARTED
            ) {
                await this.deleteGame(game.id);
                return null;
            }
        }

        return game;
    }

    async getOrCreateGame(eventId) {
        let currentGame = await this.getGameByEventId(eventId);
        let currentContext;

        currentGame = await this.deleteGameWhenEventStatusChanged(currentGame);

        if (currentGame) {
            currentContext = await ContextService.getGameContext(
                currentGame.id
            );
            if (!currentContext) {
                currentContext = await ContextService.createContext(
                    currentGame.id
                );
            }

            //TODO: refactor this! We should not have sport specific logic here, but we need to transform the context to the correct format based on the sport. We should move this logic to the serializers and just call the correct serializer based on the sport code.
            if (
                currentGame.sportCode === SportCodes.Tennis ||
                currentGame.sportCode === SportCodes.WomenTennis
            ) {
                currentContext =
                    TennisSerializer.serializeGameContext(currentContext);
            } else {
                currentContext =
                    FootballSerializer.serializeGameContext(currentContext);
            }
        } else {
            const { game, gameContext } = await this._startGame(eventId);
            currentGame = game;
            currentContext = gameContext;
        }

        return { game: currentGame, gameContext: currentContext };
    }
}

export default new GameService();
